import { useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, Table, Badge, Button, Alert, Spinner, Form, Modal } from 'react-bootstrap';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import PageWrapper from 'components/PageWrapper';
import useToaster from 'utils/hooks/useToaster';
import EnrichmentDashboardApi from 'services/KjederegisteretAdminApi/EnrichmentDashboardApi';
import {
  AdapterCallLog,
  EnrichmentAdapterStatus,
  EnrichmentStagingItem,
  EnrichmentSubscription,
} from 'services/KjederegisteretAdminApi/EnrichmentDashboardApi/types';

const formatDateTime = (value: string | null) => {
  if (!value) return '–';
  return format(new Date(value), 'dd.MM.yyyy HH:mm');
};

const AdapterCallsCell = ({ calls }: { calls: AdapterCallLog[] }) => {
  if (calls.length === 0) return <span className="text-muted">–</span>;
  const sorted = [...calls].sort((a, b) => a.order - b.order);
  return (
    <>
      {sorted.map((c) => (
        <div
          key={c.sourceName}
          className="small mb-1"
          title={`Kalt med: ${c.requestSummary}\nSvar: ${c.resultSummary}`}
        >
          <Badge bg={c.success ? 'success' : 'secondary'} className="me-1">
            {c.order + 1}. {c.sourceName}
          </Badge>
          {c.citationUrl ? (
            <a href={c.citationUrl} target="_blank" rel="noopener noreferrer">
              {c.resultSummary}
            </a>
          ) : (
            <span className="text-muted">{c.resultSummary}</span>
          )}
        </div>
      ))}
    </>
  );
};

const getErrorMessage = (e: any) =>
  e?.response?.data?.detail ?? e?.response?.data ?? e?.message ?? 'Ukjent feil';

const TriggerEnrichmentCard = () => {
  const toaster = useToaster();
  const [searchValue, setSearchValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = searchValue.trim();
    if (!value) return;

    setIsSubmitting(true);
    try {
      const result = await EnrichmentDashboardApi.triggerEnrichment(value);
      toaster.success(
        'Berikelse trigget',
        `${result.enhetNavn} (EnhetId ${result.enhetId}) er lagt i køen. Kilder: ${result.sources.join(', ')}.`,
      );
      setSearchValue('');
    } catch (e) {
      toaster.error('Kunne ikke trigge berikelse', getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header>Trigge berikelse manuelt</Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Legg en enkelt enhet i berikelseskøen med det samme, uten å vente på neste automatiske
          skann. Godtar både enhetId og løpenr.
        </p>
        <Form onSubmit={handleSubmit} className="d-flex align-items-start gap-2">
          <Form.Group className="mb-0" style={{ maxWidth: 240 }}>
            <Form.Control
              placeholder="Løpenr eller enhetId"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              disabled={isSubmitting}
            />
          </Form.Group>
          <Button type="submit" disabled={isSubmitting || !searchValue.trim()}>
            {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Trigg berikelse'}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
};

const AdaptersCard = () => {
  const toaster = useToaster();
  const [adapters, setAdapters] = useState<EnrichmentAdapterStatus[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingSourceName, setPendingSourceName] = useState<string | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    EnrichmentDashboardApi.getAdapters()
      .then(setAdapters)
      .catch((e) => setError(getErrorMessage(e)))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(load, [load]);

  const handleToggle = async (sourceName: string, enabled: boolean) => {
    setPendingSourceName(sourceName);
    try {
      const updated = await EnrichmentDashboardApi.setAdapterEnabled(sourceName, enabled);
      setAdapters(updated);
      toaster.success('Lagret', `${sourceName} er nå ${enabled ? 'slått på' : 'slått av'}.`);
    } catch (e) {
      toaster.error('Kunne ikke oppdatere adapter', getErrorMessage(e));
    } finally {
      setPendingSourceName(null);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header>Kildeadaptere</Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Slår adaptere av/på i sanntid uten deploy. "Base" er standardverdien fra konfigurasjon; en
          override her overstyrer den til neste gang overriden fjernes.
        </p>
        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {adapters && (
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Kilde</th>
                <th>Base</th>
                <th>Aktiv</th>
                <th>Sist endret</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {adapters.map((a) => (
                <tr key={a.sourceName}>
                  <td>{a.sourceName}</td>
                  <td>
                    <Badge
                      bg={a.baseEnabled ? 'secondary' : 'light'}
                      text={a.baseEnabled ? undefined : 'dark'}
                    >
                      {a.baseEnabled ? 'På' : 'Av'}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={a.effective ? 'success' : 'secondary'}>
                      {a.effective ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </td>
                  <td className="text-muted small">
                    {a.updatedBy ? `${a.updatedBy}, ${formatDateTime(a.updatedAt)}` : '–'}
                  </td>
                  <td>
                    <Form.Check
                      type="switch"
                      checked={a.effective}
                      disabled={pendingSourceName === a.sourceName}
                      onChange={(e) => handleToggle(a.sourceName, e.target.checked)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

const subscriptionStatusVariant: Record<string, string> = {
  NotConfigured: 'secondary',
  PendingSetup: 'warning',
  Configured: 'success',
  Expired: 'danger',
};

const EditSubscriptionModal = ({
  subscription,
  onClose,
  onSaved,
}: {
  subscription: EnrichmentSubscription | null;
  onClose: () => void;
  onSaved: (updated: EnrichmentSubscription) => void;
}) => {
  const toaster = useToaster();
  const [status, setStatus] = useState('NotConfigured');
  const [planOrTier, setPlanOrTier] = useState('');
  const [keyVaultSecretReference, setKeyVaultSecretReference] = useState('');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!subscription) return;
    setStatus(subscription.status);
    setPlanOrTier(subscription.planOrTier ?? '');
    setKeyVaultSecretReference(subscription.keyVaultSecretReference ?? '');
    setNotes(subscription.notes ?? '');
  }, [subscription]);

  const handleSave = async () => {
    if (!subscription) return;
    setIsSaving(true);
    try {
      const updated = await EnrichmentDashboardApi.setSubscription(subscription.dependencyName, {
        status,
        planOrTier: planOrTier || null,
        keyVaultSecretReference: keyVaultSecretReference || null,
        notes: notes || null,
      });
      onSaved(updated);
      toaster.success('Lagret', `${subscription.dependencyName} er oppdatert.`);
      onClose();
    } catch (e) {
      toaster.error('Kunne ikke lagre', getErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={subscription !== null} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{subscription?.dependencyName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Status</Form.Label>
          <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="NotConfigured">Ikke konfigurert</option>
            <option value="PendingSetup">Under oppsett</option>
            <option value="Configured">Konfigurert</option>
            <option value="Expired">Utløpt</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Plan/nivå</Form.Label>
          <Form.Control value={planOrTier} onChange={(e) => setPlanOrTier(e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Key Vault-referanse</Form.Label>
          <Form.Control
            value={keyVaultSecretReference}
            onChange={(e) => setKeyVaultSecretReference(e.target.value)}
            placeholder="f.eks. kv-snitchapi-prod/RetrieverApiKey"
          />
          <Form.Text className="text-muted">
            Kun en referanse til hvor nøkkelen ligger - ikke selve nøkkelverdien.
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Notater</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          Avbryt
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Spinner animation="border" size="sm" /> : 'Lagre'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const SubscriptionsCard = () => {
  const [subscriptions, setSubscriptions] = useState<EnrichmentSubscription[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EnrichmentSubscription | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    EnrichmentDashboardApi.getSubscriptions()
      .then(setSubscriptions)
      .catch((e) => setError(getErrorMessage(e)))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(load, [load]);

  return (
    <Card className="mb-4">
      <Card.Header>Eksterne avhengigheter</Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Status for abonnementer/app-registreringer som News- og Facebook-adapterne er avhengige
          av. Kun referanser lagres her - selve nøklene ligger i Key Vault.
        </p>
        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {subscriptions && (
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Avhengighet</th>
                <th>Status</th>
                <th>Plan/nivå</th>
                <th>Notater</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((s) => (
                <tr key={s.dependencyName}>
                  <td>{s.dependencyName}</td>
                  <td>
                    <Badge bg={subscriptionStatusVariant[s.status] ?? 'secondary'}>
                      {s.status}
                    </Badge>
                  </td>
                  <td>{s.planOrTier ?? '–'}</td>
                  <td className="text-muted small">{s.notes ?? '–'}</td>
                  <td>
                    <Button variant="outline-secondary" size="sm" onClick={() => setEditing(s)}>
                      Rediger
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <EditSubscriptionModal
          subscription={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) =>
            setSubscriptions((prev) =>
              (prev ?? []).map((s) => (s.dependencyName === updated.dependencyName ? updated : s)),
            )
          }
        />
      </Card.Body>
    </Card>
  );
};

const StagingQueueCard = () => {
  const toaster = useToaster();
  const [items, setItems] = useState<EnrichmentStagingItem[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    EnrichmentDashboardApi.getStagingItems()
      .then(setItems)
      .catch((e) => setError(getErrorMessage(e)))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(load, [load]);

  const handleReview = async (item: EnrichmentStagingItem, status: 'SentToQa' | 'Discarded') => {
    setPendingId(item.id);
    try {
      await EnrichmentDashboardApi.reviewStagingItem(item.id, status);
      setItems((prev) => (prev ?? []).filter((i) => i.id !== item.id));
      toaster.success(
        'Behandlet',
        status === 'SentToQa'
          ? `${item.enhetNavn} er sendt til QA.`
          : `${item.enhetNavn} er forkastet.`,
      );
    } catch (e) {
      toaster.error('Kunne ikke behandle forslag', getErrorMessage(e));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Forslag til gjennomgang{items ? ` (${items.length})` : ''}</span>
        <Button variant="outline-secondary" size="sm" onClick={load} disabled={isLoading}>
          Oppdater
        </Button>
      </Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Ingenting herfra går til QA automatisk ennå - hvert forslag må godkjennes eller forkastes
          manuelt mens pipelinen bevises ut.
        </p>
        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {items && items.length === 0 && !isLoading && (
          <p className="text-muted">Ingen forslag venter på gjennomgang.</p>
        )}
        {items && items.length > 0 && (
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Enhet</th>
                <th>Endringer</th>
                <th>Kilder</th>
                <th>Adaptere</th>
                <th>AI-oppsummering</th>
                <th>Opprettet</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.enhetId ? (
                      <Link
                        to={`/enheter/${item.enhetId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.enhetNavn}
                      </Link>
                    ) : (
                      item.enhetNavn
                    )}
                    <div className="text-muted small">{item.orgNr ?? item.bedriftsNr}</div>
                  </td>
                  <td>
                    {item.diffs.map((d) => (
                      <div key={d.fieldName} className="small mb-1">
                        <strong>{d.fieldName}:</strong> {d.oldValue ?? '(tomt)'} → {d.newValue}
                        {d.confidence != null && (
                          <span className="text-muted">
                            {' '}
                            ({Math.round(d.confidence * 100)}% tillit)
                          </span>
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    {item.sources.map((s) =>
                      s.citationUrl ? (
                        <a
                          key={s.source}
                          href={s.citationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="me-1"
                        >
                          <Badge bg="info">{s.source}</Badge>
                        </a>
                      ) : (
                        <Badge bg="info" key={s.source} className="me-1">
                          {s.source}
                        </Badge>
                      ),
                    )}
                  </td>
                  <td>
                    <AdapterCallsCell calls={item.adapterCalls} />
                  </td>
                  <td className="text-muted small" style={{ maxWidth: 260 }}>
                    {item.aiSummary ?? '–'}
                  </td>
                  <td className="text-muted small">{formatDateTime(item.createdAt)}</td>
                  <td className="text-nowrap">
                    <Button
                      variant="success"
                      size="sm"
                      className="me-1"
                      disabled={pendingId === item.id}
                      onClick={() => handleReview(item, 'SentToQa')}
                    >
                      Send til QA
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={pendingId === item.id}
                      onClick={() => handleReview(item, 'Discarded')}
                    >
                      Forkast
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

const historyStatusVariant: Record<string, string> = {
  PendingReview: 'warning',
  SentToQa: 'success',
  Discarded: 'secondary',
  NoChangeFound: 'light',
  Failed: 'danger',
};

const historyStatusLabel: Record<string, string> = {
  PendingReview: 'Venter på gjennomgang',
  SentToQa: 'Sendt til QA',
  Discarded: 'Forkastet',
  NoChangeFound: 'Ingen endring funnet',
  Failed: 'Feilet',
};

const HistoryCard = () => {
  const [items, setItems] = useState<EnrichmentStagingItem[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    EnrichmentDashboardApi.getHistory()
      .then(setItems)
      .catch((e) => setError(getErrorMessage(e)))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(load, [load]);

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Siste kjøringer</span>
        <Button variant="outline-secondary" size="sm" onClick={load} disabled={isLoading}>
          Oppdater
        </Button>
      </Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Alle enheter pipelinen har sjekket, uansett utfall - inkludert de hvor ingen endring ble
          funnet, slik at &quot;sjekket, fant ingenting&quot; ikke blir umulig å skille fra
          &quot;aldri sjekket&quot;.
        </p>
        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {items && items.length === 0 && !isLoading && (
          <p className="text-muted">Ingen kjøringer registrert ennå.</p>
        )}
        {items && items.length > 0 && (
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Enhet</th>
                <th>Status</th>
                <th>Endringer</th>
                <th>Adaptere</th>
                <th>Opprettet</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.enhetId ? (
                      <Link
                        to={`/enheter/${item.enhetId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.enhetNavn}
                      </Link>
                    ) : (
                      item.enhetNavn
                    )}
                    <div className="text-muted small">{item.orgNr ?? item.bedriftsNr}</div>
                  </td>
                  <td>
                    <Badge
                      bg={historyStatusVariant[item.status] ?? 'secondary'}
                      text={item.status === 'NoChangeFound' ? 'dark' : undefined}
                    >
                      {historyStatusLabel[item.status] ?? item.status}
                    </Badge>
                  </td>
                  <td className="small">
                    {item.diffs.length > 0 ? (
                      item.diffs.map((d) => (
                        <div key={d.fieldName}>
                          {d.fieldName}: {d.oldValue ?? '(tomt)'} → {d.newValue}
                        </div>
                      ))
                    ) : item.status === 'Failed' && item.aiSummary ? (
                      <span className="text-danger">Årsak: {item.aiSummary}</span>
                    ) : (
                      '–'
                    )}
                  </td>
                  <td>
                    <AdapterCallsCell calls={item.adapterCalls} />
                  </td>
                  <td className="text-muted small">{formatDateTime(item.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

const EnrichmentDashboard = () => {
  return (
    <PageWrapper header="Databerikelse">
      <Row className="mb-3">
        <Col>
          <p className="text-muted">
            Overvåker enheter med manglende data (f.eks. oppstartsdato) og foreslår oppdateringer
            basert på eksterne kilder. Se plandokumentet for detaljer om identifisering, berikelse,
            sammenligning og beslutning.
          </p>
        </Col>
      </Row>
      <Row>
        <Col>
          <TriggerEnrichmentCard />
        </Col>
      </Row>
      <Row>
        <Col>
          <AdaptersCard />
        </Col>
      </Row>
      <Row>
        <Col>
          <SubscriptionsCard />
        </Col>
      </Row>
      <Row>
        <Col>
          <StagingQueueCard />
        </Col>
      </Row>
      <Row>
        <Col>
          <HistoryCard />
        </Col>
      </Row>
    </PageWrapper>
  );
};

export default EnrichmentDashboard;
